/**
 * Избранное: ключ игры и запись-снимок карточки. Те же правила ключей — в Visoria
 * (account/favorites.js) и в скрипте SEO-страниц каталога (seo/render.js).
 */

export const FAVORITES_STORAGE_KEY = "ts_favorites_v1";
export const MAX_FAVORITES = 100;
export const FAVORITES_FULL = "Избранное заполнено — уберите что-нибудь в кабинете";

export const PLATFORM_LABELS = {
  nintendo: "Nintendo",
  steam: "Steam",
  playstation: "PlayStation",
};

export const REGION_LABELS = {
  nintendo: {
    us: "США",
    pl: "Польша",
    fr: "Европа",
    br: "Бразилия",
    ca: "Канада",
    hk: "Гонконг",
    se: "Швеция",
  },
  steam: { kz: "Казахстан", ru: "Россия", us: "США" },
  playstation: { us: "США", pl: "Польша", in: "Индия" },
};

const SEO_PATH_RE = /^\/nintendo\/igry\/([a-z0-9-]{1,120})$/;
const STEAM_COVER_PROXY_RE = /\/api\/visoria\/steam\/cover\?u=([^&]+)/;

export function slugFromSeoPath(seoPath) {
  return SEO_PATH_RE.exec(String(seoPath || ""))?.[1] || null;
}

/** Ключ игры внутри платформы или null, если игру не опознать. */
export function favoriteKey(platform, { seoPath, nsuid, appId, id } = {}) {
  if (platform === "nintendo") {
    const slug = slugFromSeoPath(seoPath);
    if (slug) return `slug:${slug}`;
    return /^\d{6,20}$/.test(String(nsuid || "")) ? `nsuid:${nsuid}` : null;
  }
  if (platform === "steam") {
    return /^\d{1,10}$/.test(String(appId || "")) ? `app:${appId}` : null;
  }
  if (platform === "playstation") {
    return /^[A-Za-z0-9_.:-]{1,120}$/.test(String(id || "")) ? `ps:${id}` : null;
  }
  return null;
}

export const keyOf = (item) => `${item.platform}|${item.gameKey}`;

/** Обложка Steam хранится исходной ссылкой CDN, а не адресом нашего прокси. */
function rawImage(url) {
  const match = STEAM_COVER_PROXY_RE.exec(String(url || ""));
  if (!match) return url || null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

function priced(currency, price, original, percent) {
  if (!currency || typeof price !== "number" || !Number.isFinite(price)) return {};
  const discounted = typeof original === "number" && original > price;
  return {
    currency,
    price,
    original: discounted ? original : null,
    percent: discounted && Number.isInteger(percent) ? percent : null,
  };
}

function record(platform, gameKey, fields) {
  return gameKey ? { platform, gameKey, ...fields } : null;
}

/** Карточка витрины скидок (mapDeal) → запись избранного. */
export function favoriteFromDeal(platform, deal, region) {
  const base = {
    region,
    title: deal.name,
    image: rawImage(deal.cover),
    ...priced(deal.currency, deal.amount, deal.originalAmount, deal.discountPercent),
  };
  if (platform === "nintendo") {
    return record(platform, favoriteKey(platform, deal), {
      ...base,
      nsuid: deal.nsuid || null,
      seoPath: deal.seoPath || null,
      url: deal.seoPath || deal.link,
    });
  }
  if (platform === "steam") {
    return record(platform, favoriteKey(platform, { appId: deal.nsuid }), {
      ...base,
      appId: deal.nsuid,
      url: deal.storeUrl || deal.link,
    });
  }
  return record(platform, favoriteKey(platform, { id: deal.id }), {
    ...base,
    url: deal.storeUrl || deal.link,
  });
}

/** Игра Nintendo из поиска или панели покупки. */
export function favoriteFromNintendoGame(game, region, currency) {
  return record("nintendo", favoriteKey("nintendo", game), {
    region,
    title: game.title,
    image: game.image || null,
    nsuid: game.nsuid || null,
    seoPath: game.seoPath || null,
    url: game.seoPath || null,
    ...priced(currency, game.price, game.original, game.percent),
  });
}

/** Игра Steam из поиска или панели покупки. */
export function favoriteFromSteamGame(game, region) {
  return record("steam", favoriteKey("steam", game), {
    region,
    title: game.title,
    image: rawImage(game.cover || game.image),
    appId: game.appId,
    url: game.url || null,
    ...priced(game.currency, game.price, game.original, game.percent),
  });
}

/** Выбранная игра PlayStation ({ game, region, currency }). */
export function favoriteFromPsPick(pick) {
  const { game } = pick;
  return record("playstation", favoriteKey("playstation", game), {
    region: pick.region,
    title: game.title,
    image: game.image || null,
    url: game.storeUrl || null,
    ...priced(pick.currency, game.price, game.original, game.percent),
  });
}
